/**
 * The legacy integer-dollar engine, preserved bit-for-bit.
 *
 * Bookings created before the decimal cutover are priced here forever, so this
 * file must keep producing exactly the numbers it produces today — rounding
 * quirks included. Every oddity below is deliberate:
 *
 *   - `parseInt(cleaning_type)` truncates a decimal base price;
 *   - `Math.round` wraps the *whole* frequency expression, so the subtotal is
 *     quantised to whole dollars before the flexible, promo and add-on steps;
 *   - tax is rounded to the nearest dollar, not the nearest cent.
 *
 * Do not "fix" anything here. Fixing it changes the price of a booking that has
 * already been sold. The corrected arithmetic lives in ./decimal.ts.
 */

import {
  ADD_ON_COST_CENTS,
  CleaningTypeId,
  ROOM_COST_CENTS,
  getFrequencyDiscountPercent,
  getRateCard,
  isFlexible,
} from "./rates";

export interface LegacyBookingInput {
  bedrooms: string | number;
  bathrooms: string | number;
  /** The legacy wire format: the cleaning type's price, as a string. */
  cleaning_type: string;
  clean_time: string;
  frequency: string;
  add_ons?: string[] | null;
  created_at: Date;
}

const dollars = (cents: number) => cents / 100;

/** Add-on cost in whole dollars, reproducing `getAddOnsCost` exactly. */
export function getLegacyAddOnsCost(
  cleaningCostDollars: number,
  addOns: string[] | null | undefined,
  createdAt: Date
): number {
  const card = getRateCard(createdAt);
  const moveInOut = dollars(card.cleaningTypes.move_in_out);
  let cost = 0;
  for (const addOn of addOns || []) {
    const name = addOn.toLowerCase();
    if (name.includes("organizing")) cost += dollars(ADD_ON_COST_CENTS.organizing);
    else if (name.includes("laundry")) cost += dollars(ADD_ON_COST_CENTS.laundry);
    else if (name.includes("windows")) cost += dollars(ADD_ON_COST_CENTS.windows);
    else if (name.includes("cabinet")) {
      if (cleaningCostDollars < moveInOut) cost += dollars(ADD_ON_COST_CENTS.cabinets);
    } else if (name.trim() && cleaningCostDollars < moveInOut) {
      cost += dollars(ADD_ON_COST_CENTS.oven);
    }
  }
  return cost;
}

/** Sales tax in whole dollars, reproducing `getTax` exactly. */
export function getLegacyTax(subTotalDollars: number): number {
  return Math.round((subTotalDollars * 8.875) / 100);
}

/** Room charge in whole dollars. The legacy engine bills bedrooms and bathrooms alike. */
function roomsCost(bedrooms: string | number, bathrooms: string | number): number {
  return (
    parseInt(String(bedrooms), 10) * dollars(ROOM_COST_CENTS) +
    Number(bathrooms) * dollars(ROOM_COST_CENTS)
  );
}

/** Rooms plus cleaning cost, in whole dollars, reproducing `getAppointmentValue`. */
export function getLegacyAppointmentValue(
  bathrooms: string | number,
  bedrooms: string | number,
  cleaningType: CleaningTypeId,
  createdAt: Date
): number {
  const card = getRateCard(createdAt);
  return roomsCost(bedrooms, bathrooms) + dollars(card.cleaningTypes[cleaningType]);
}

/**
 * Every line of a legacy booking, in whole dollars.
 *
 * These are the intermediate values of the legacy computation itself, so they
 * sum to what was charged by construction. Surfaces that want to *show* the
 * arithmetic must use these rather than recomputing the lines on the cents
 * engine, which quantises differently and so does not reconcile to a legacy
 * total.
 *
 * `frequencyDiscount` is reported as `base - round(base - pct)` rather than the
 * exact percentage, because that rounded step is what the subtotal was built
 * from.
 */
export interface LegacyBreakdown {
  /** Rooms plus the cleaning type, before any discount. */
  base: number;
  rooms: number;
  cleaningType: number;
  frequencyDiscount: number;
  flexibleDiscount: number;
  promoDiscount: number;
  addOns: number;
  subTotal: number;
  tax: number;
  total: number;
}

/** The legacy engine's own intermediate values. See {@link LegacyBreakdown}. */
export function getLegacyBreakdown(
  input: LegacyBookingInput,
  codeDiscountDollars = 0
): LegacyBreakdown {
  const cleaningCost = parseInt(input.cleaning_type, 10);
  const rooms = roomsCost(input.bedrooms, input.bathrooms);
  const base = rooms + cleaningCost;

  // `Math.round` wraps the whole expression, exactly as it always has — the
  // subtotal is quantised to whole dollars here, before the remaining steps.
  const afterFrequency = Math.round(
    base - (getFrequencyDiscountPercent(input.frequency) * base) / 100
  );
  const frequencyDiscount = base - afterFrequency;

  const flexibleDiscount = isFlexible(input.clean_time) ? 10 : 0;
  const addOns = getLegacyAddOnsCost(
    cleaningCost,
    input.add_ons,
    input.created_at
  );

  const subTotal =
    afterFrequency - flexibleDiscount - codeDiscountDollars + addOns;
  const tax = getLegacyTax(subTotal);

  return {
    base,
    rooms,
    cleaningType: cleaningCost,
    frequencyDiscount,
    flexibleDiscount,
    promoDiscount: codeDiscountDollars,
    addOns,
    subTotal,
    tax,
    total: subTotal + tax,
  };
}

/**
 * The legacy total, in whole dollars. Byte-for-byte equivalent to
 * `apps/api/src/lib/pricing.ts#getTotalPrice`.
 *
 * Delegates to {@link getLegacyBreakdown} so the total and the lines shown
 * beside it cannot drift apart — the operation order there is unchanged from
 * the original implementation of this function.
 */
export function getLegacyTotalPrice(
  input: LegacyBookingInput,
  codeDiscountDollars = 0
): number {
  return getLegacyBreakdown(input, codeDiscountDollars).total;
}
